import { getIdCardError, isValidIdCard } from '../../../shared/utils/idCardValidation';
import { portalFetch } from '../../../shared/auth/portalSession';
import React, { useState, useEffect } from 'react';
import { WEEK_DAYS } from '../../../shared/types/common';
import { getServicesApi } from '../../../shared/api/servicesApi';
import { getScheduleApi } from '../../../shared/api/scheduleApi';
import { 
  createAppointmentApi, 
  getAvailableSlotsApi,
  getAvailableDatesApi,
  type AppointmentData,
  type AvailableDate
} from '../../../shared/api/appointmentApi';
import { NORTH_ISRAEL_VILLAGES, type Village } from '../../../shared/constants/villages';
import type { SecretaryUser } from '../../../shared/api/secretaryApi';

// Get user profile from Google token
type ServiceType = 'US' | 'Xray';

interface Service {
  id: string;
  name: string;
  availableDays: string[];
}

interface TimeSlot {
  time: string;
  isAvailable: boolean;
  intervalMinutes?: number;
}

interface BookingFormData {
  // Step 1: Patient Info
  fullName: string;
  phoneNumber: string;
  idCard: string;
  village: string;
  
  // Step 2: Service Type
  serviceType: ServiceType | null;
  
  // Step 3: Service (US only)
  serviceId: string;
  serviceName: string;
  
  // Step 4: Day
  selectedDay: string;
  selectedDayLabel: string;
  
  // Step 5: Time
  selectedTime: string;
  intervalMinutes?: number; // Store interval from selected slot
}

interface BookingFlowProps {
  user: SecretaryUser;
}

const BookingFlow: React.FC<BookingFlowProps> = ({ user }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<BookingFormData>({
    fullName: '',
    phoneNumber: '',
    idCard: '',
    village: '',
    serviceType: null,
    serviceId: '',
    serviceName: '',
    selectedDay: '',
    selectedDayLabel: '',
    selectedTime: '',
  });

  const [filteredVillages, setFilteredVillages] = useState<Village[]>([]);
  const [showVillageDropdown, setShowVillageDropdown] = useState(false);
  const [services, setServices] = useState<Service[]>([]);
  const [availableDates, setAvailableDates] = useState<AvailableDate[]>([]);
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  const [xraySchedule, setXraySchedule] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draftId, setDraftId] = useState<string | undefined>(undefined);
  const [duplicateAppointment, setDuplicateAppointment] = useState<AppointmentData | null>(null);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);

  const [idCardTouched, setIdCardTouched] = useState(false);
  const idCardError = getIdCardError(formData.idCard);
  const visibleIdCardError = idCardTouched ? idCardError : null;

  const totalSteps = formData.serviceType === 'Xray' ? 3 : 5;

  // Village autocomplete
  useEffect(() => {
    if (formData.village) {
      const filtered = NORTH_ISRAEL_VILLAGES.filter(v =>
        v.name.includes(formData.village)
      );
      setFilteredVillages(filtered);
    } else {
      setFilteredVillages([]);
    }
  }, [formData.village]);

  // Auto-load services when on step 3 (service selection) for US
  useEffect(() => {
    if (currentStep === 3 && formData.serviceType === 'US' && services.length === 0) {
      loadServices();
    }
  }, [currentStep, formData.serviceType]);

  // Auto-load available dates when on step 4 (date selection) for US
  useEffect(() => {
    if (currentStep === 4 && formData.serviceType === 'US' && formData.serviceId && availableDates.length === 0) {
      loadAvailableDays();
    }
  }, [currentStep, formData.serviceType, formData.serviceId]);

  // Auto-load time slots when on step 5 (time selection) for US
  useEffect(() => {
    if (currentStep === 5 && formData.serviceType === 'US' && formData.selectedDay && availableSlots.length === 0) {
      loadAvailableSlots(formData.selectedDay);
    }
  }, [currentStep, formData.serviceType, formData.selectedDay]);

  const loadServices = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getServicesApi();
      console.log('Services API response:', data);
      const activeServices = data.filter(s => s.is_active).map(s => ({
        id: s.service_id,
        name: s.service_name,
        availableDays: s.supported_days || [],
      }));
      console.log('Mapped active services:', activeServices);
      setServices(activeServices);
    } catch (err: any) {
      console.error('Error loading services:', err);
      setError('שגיאה בטעינת השירותים. אנא נסה שוב.');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableDays = async () => {
    try {
      setLoading(true);
      setError(null);
      const scheduleType = formData.serviceType === 'US' ? 2 : 1;
      
      // For Xray, load the schedule to show working hours
      if (formData.serviceType === 'Xray') {
        const schedule = await getScheduleApi(1); // 1 = Xray
        setXraySchedule(schedule);
        // Skip loading available dates for Xray
        setLoading(false);
        return;
      }
      
      // Use the API that returns only dates with available slots
      const dates = await getAvailableDatesApi(
        scheduleType,
        formData.serviceType === 'US' ? formData.serviceId : undefined
      );
      
      // Sort dates with today first
      const today = new Date().toISOString().split('T')[0];
      const sortedDates = [...dates].sort((a, b) => {
        if (a.date === today) return -1;
        if (b.date === today) return 1;
        return new Date(a.date).getTime() - new Date(b.date).getTime();
      });
      
      setAvailableDates(sortedDates);
    } catch (err: any) {
      console.error('Error loading available days:', err);
      setError('שגיאה בטעינת הימים הזמינים. אנא נסה שוב.');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableSlots = async (day: string) => {
    try {
      setLoading(true);
      setError(null);
      const scheduleType = formData.serviceType === 'US' ? 2 : 1;
      const slots = await getAvailableSlotsApi(day, scheduleType, formData.serviceId);
      setAvailableSlots(slots);
    } catch (err: any) {
      console.error('Error loading available slots:', err);
      setError('שגיאה בטעינת השעות הזמינות. אנא נסה שוב.');
    } finally {
      setLoading(false);
    }
  };

  const saveDraft = async () => {
    try {
      // Only save if we have at least basic patient info filled
      if (!formData.fullName || !formData.phoneNumber || !formData.village || !isValidIdCard(formData.idCard)) {
        return;
      }

      const appointmentData: AppointmentData = {
        appointmentId: draftId, // Include existing draft ID for updates
        fullName: formData.fullName,
        phoneNumber: formData.phoneNumber,
        idCard: formData.idCard || '',
        village: formData.village,
        provider: user.provider,
        serviceType: formData.serviceType === 'Xray' ? 1 : 2, // Default to 2 (US)
        serviceId: formData.serviceId || '',
        serviceName: formData.serviceName || '',
        selectedDay: formData.selectedDay || '',
        selectedDayLabel: formData.selectedDayLabel || '',
        selectedTime: formData.selectedTime || '',
        status: 'draft',
        createdBy: user.name,
        clinicName: user.clinicName,
      };
      
      const result = await createAppointmentApi(appointmentData);
      // Save the returned appointment ID for future updates
      if (result.appointmentId && !draftId) {
        setDraftId(result.appointmentId);
      }
    } catch (err) {
      console.error('Failed to save draft:', err);
      // Don't show error to user for draft saves
    }
  };

  const checkForDuplicateAppointment = async (): Promise<boolean> => {
    // Only check if we have ID card and service selected
    if (!formData.idCard || !formData.serviceId) {
      return false;
    }

    try {
      const { getApiBaseUrl } = await import('../../../shared/api/config');
      const response = await portalFetch('secretary', `${getApiBaseUrl()}/appointments/by-idcard?idCard=${formData.idCard}&serviceId=${formData.serviceId}&futureOnly=true`);
      
      if (response.ok) {
        const appointments: AppointmentData[] = await response.json();
        // Filter out current draft
        const duplicate = appointments.find(apt => apt.appointmentId !== draftId);
        
        if (duplicate) {
          // Delete the draft appointment since we found a duplicate
          if (draftId) {
            try {
              const { deleteAppointmentApi } = await import('../../../shared/api/appointmentApi');
              await deleteAppointmentApi(draftId);
              setDraftId(undefined);
            } catch (deleteErr) {
              console.error('Failed to delete draft:', deleteErr);
            }
          }
          
          setDuplicateAppointment(duplicate);
          setShowDuplicateModal(true);
          return true; // Duplicate found
        }
      }
    } catch (err) {
      console.error('Error checking for duplicates:', err);
    }
    
    return false; // No duplicate found
  };

  const handleNext = async () => {
    if (currentStep === 1) {
      setIdCardTouched(true);
      if (idCardError) {
        setError(idCardError);
        return;
      }
      if (!formData.fullName || !formData.phoneNumber || !formData.village) {
        setError('נא למלא את כל השדות');
        return;
      }
      // Validate phone number is exactly 10 digits
      const phoneDigits = formData.phoneNumber.replace(/\D/g, '');
      if (phoneDigits.length !== 10) {
        setError('מספר טלפון חייב להכיל בדיוק 10 ספרות');
        return;
      }
    } else if (currentStep === 2) {
      if (!formData.serviceType) {
        setError('נא לבחור סוג שירות');
        return;
      }
      if (formData.serviceType === 'US') {
        loadServices();
      } else {
        loadAvailableDays();
      }
    } else if (currentStep === 3 && formData.serviceType === 'US') {
      if (!formData.serviceId) {
        setError('נא לבחור שירות');
        return;
      }
      
      // Check for duplicate appointment after service selection
      setLoading(true);
      const hasDuplicate = await checkForDuplicateAppointment();
      setLoading(false);
      
      if (hasDuplicate) {
        return; // Stop here if duplicate found
      }
      
      loadAvailableDays();
    } else if (currentStep === 3 && formData.serviceType === 'Xray') {
      if (!formData.selectedDay) {
        setError('נא לבחור יום');
        return;
      }
      loadAvailableSlots(formData.selectedDay);
    } else if (currentStep === 4 && formData.serviceType === 'US') {
      if (!formData.selectedDay) {
        setError('נא לבחור יום');
        return;
      }
      loadAvailableSlots(formData.selectedDay);
    }

    setError(null);
    
    // Auto-save draft after each step (if we have basic patient info)
    if (formData.fullName && formData.phoneNumber && formData.village) {
      await saveDraft();
    }
    
    if (currentStep === 2 && formData.serviceType === 'Xray') {
      setCurrentStep(3);
    } else {
      setCurrentStep((prev: number) => Math.min(prev + 1, totalSteps));
    }
  };

  const handleBack = () => {
    if (currentStep === 3 && formData.serviceType === 'Xray') {
      setCurrentStep(2);
    } else {
      setCurrentStep((prev: number) => Math.max(prev - 1, 1));
    }
  };

  const isCurrentStepValid = (): boolean => {
    // Disable if loading
    if (loading) return false;
    
    switch (currentStep) {
      case 1:
        return !!(
          formData.fullName &&
          formData.phoneNumber &&
          formData.idCard &&
          formData.village &&
          formData.phoneNumber.replace(/\D/g, '').length === 10 &&
          isValidIdCard(formData.idCard)
        );
      case 2:
        return !!formData.serviceType;
      case 3:
        if (formData.serviceType === 'US') {
          // Disable if no services loaded or no service selected
          return services.length > 0 && !!formData.serviceId;
        }
        // For Xray on step 3, no validation needed (just showing working hours)
        return true;
      case 4:
        if (formData.serviceType === 'US') {
          // Disable if no dates loaded or no day selected
          return availableDates.length > 0 && !!formData.selectedDay;
        }
        // For Xray on step 4, need time slot selected
        return availableSlots.length > 0 && !!formData.selectedTime;
      case 5:
        // For US on step 5, need time slot selected
        return availableSlots.length > 0 && !!formData.selectedTime;
      default:
        return true;
    }
  };

  const handleSubmit = async () => {
    // For Xray, skip appointment saving and just reset to beginning
    if (formData.serviceType === 'Xray') {
      setDraftId(undefined);
      setIdCardTouched(false);
      setFormData({
        fullName: '',
        phoneNumber: '',
        idCard: '',
        village: '',
        serviceType: null,
        serviceId: '',
        serviceName: '',
        selectedDay: '',
        selectedDayLabel: '',
        selectedTime: '',
      });
      setCurrentStep(1);
      return;
    }

    if (!formData.selectedTime) {
      setError('נא לבחור שעה');
      return;
    }

    if (!formData.serviceType) {
      setError('נא לבחור סוג שירות');
      return;
    }

    await proceedWithBooking();
  };

  const proceedWithBooking = async () => {
    setIdCardTouched(true);
    if (idCardError) {
      setError(idCardError);
      setCurrentStep(1);
      return;
    }

    try {
      setLoading(true);
      const appointmentData: AppointmentData = {
        appointmentId: draftId, // Include draft ID to update existing record
        fullName: formData.fullName,
        phoneNumber: formData.phoneNumber,
        idCard: formData.idCard || '',
        village: formData.village,
        provider: user.provider,
        serviceType: 2, // US service (Xray appointments are not saved)
        serviceId: formData.serviceId,
        serviceName: formData.serviceName,
        selectedDay: formData.selectedDay,
        selectedDayLabel: formData.selectedDayLabel,
        selectedTime: formData.selectedTime,
        intervalMinutes: formData.intervalMinutes || 5, // Store interval to preserve original schedule
        status: 'completed',
        createdBy: user.name,
        clinicName: user.clinicName,
      };

      await createAppointmentApi(appointmentData);
      
      setLoading(false);
      

      
      // Reset form and go to blank state
      setCurrentStep(0); // Use 0 to show blank/success screen
      
      // After 2 seconds, hide toast and go to beginning
      setTimeout(() => {
        setDraftId(undefined);
        setIdCardTouched(false);
        setFormData({
          fullName: '',
          phoneNumber: '',
          idCard: '',
          village: '',
          serviceType: null,
          serviceId: '',
          serviceName: '',
          selectedDay: '',
          selectedDayLabel: '',
          selectedTime: '',
        });
        setCurrentStep(1);
      }, 3000);
    } catch (err) {
      setError('שמירת התור נכשלה');
    } finally {
      setLoading(false);
    }
  };

  const renderStepIndicator = () => {
    const getStepLabel = (step: number) => {
      if (step === 1) return 'פרטים אישיים';
      if (step === 2) return 'סוג שירות';
      if (step === 3 && formData.serviceType === 'US') return 'בחירת שירות';
      if (step === 3 && formData.serviceType === 'Xray') return 'בחירת יום';
      if (step === 4 && formData.serviceType === 'US') return 'בחירת יום';
      if (step === 4 && formData.serviceType === 'Xray') return 'בחירת שעה';
      if (step === 5) return 'בחירת שעה';
      return '';
    };

    return (
      <div className="mb-10">
        {/* Step labels */}
        <div className="flex justify-between mb-3 px-2">
          {Array.from({ length: totalSteps }, (_, i) => i + 1).map((step) => (
            <div key={step} className="flex-1 text-center">
              <span className={`text-sm font-semibold transition-colors ${
                step === currentStep ? 'text-teal-600' : 'text-slate-500'
              }`}>
                {getStepLabel(step)}
              </span>
            </div>
          ))}
        </div>
        
        {/* Progress bar */}
        <div className="relative">
          <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-teal-500 to-blue-600 transition-all duration-500 ease-out rounded-full"
              style={{ width: `${(currentStep / totalSteps) * 100}%` }}
            />
          </div>
          
          {/* Step markers */}
          <div className="absolute top-0 left-0 right-0 flex justify-between px-0">
            {Array.from({ length: totalSteps }, (_, i) => i + 1).map((step) => (
              <div 
                key={step}
                className="relative flex items-center justify-center -mt-1"
                style={{ marginLeft: step === 1 ? '0' : '-12px', marginRight: step === totalSteps ? '0' : '-12px' }}
              >
                <div
                  className={`w-5 h-5 rounded-full border-3 transition-all duration-300 ${
                    step < currentStep
                      ? 'bg-green-500 border-green-500 scale-100'
                      : step === currentStep
                      ? 'bg-white border-teal-500 scale-125 shadow-lg'
                      : 'bg-white border-slate-300 scale-90'
                  }`}
                >
                  {step < currentStep && (
                    <svg className="w-full h-full text-white p-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={4} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderStep1 = () => (
    <div className="space-y-4 sm:space-y-6 animate-fade-in">
      <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
        <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-teal-400 to-blue-500 rounded-lg flex items-center justify-center">
          <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        </div>
        <h3 className="text-xl sm:text-2xl font-bold text-slate-800">פרטי המטופל</h3>
      </div>
              <div className="group">
          <label className="block text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
            <svg className="w-4 h-4 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
            </svg>
            תעודת זהות *
          </label>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]{9}"
            autoFocus
            required
            aria-invalid={Boolean(visibleIdCardError)}
            aria-describedby="booking-id-card-error"
            onBlur={() => setIdCardTouched(true)}
            value={formData.idCard}
            onChange={(e) => {
              // Only allow numeric input and limit to 9 digits
              const value = e.target.value.replace(/\D/g, '').slice(0, 9);
              setFormData({ ...formData, idCard: value });
            }}
            onKeyPress={(e) => {
              // Prevent non-numeric characters from being typed
              if (!/[0-9]/.test(e.key)) {
                e.preventDefault();
              }
            }}
            className="w-full px-3 sm:px-4 py-2.5 sm:py-3.5 border-2 border-slate-200 rounded-xl focus:ring-4 focus:ring-teal-100 focus:border-teal-500 transition-all duration-200 text-base sm:text-lg group-hover:border-slate-300"
            placeholder="מספר תעודת זהות (חובה)"
            dir="ltr"
            maxLength={9}
          />
          <p id="booking-id-card-error" role={visibleIdCardError ? 'alert' : undefined} className={`text-xs mt-1 ${visibleIdCardError ? 'text-red-600' : 'text-slate-500'}`}>
            {visibleIdCardError || 'יש להזין 9 ספרות בלבד'}
          </p>
        </div>
      <div className="space-y-3 sm:space-y-4">
        <div className="group">
          <label className="block text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
            <svg className="w-4 h-4 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            שם מלא *
          </label>
          <input
            type="text"
            value={formData.fullName}
            onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
            className="w-full px-3 sm:px-4 py-2.5 sm:py-3.5 border-2 border-slate-200 rounded-xl focus:ring-4 focus:ring-teal-100 focus:border-teal-500 transition-all duration-200 text-base sm:text-lg group-hover:border-slate-300"
            placeholder="הכנס שם מלא"
          />
        </div>
        
        <div className="group">
          <label className="block text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
            <svg className="w-4 h-4 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
            מספר טלפון *
          </label>
          <input
            type="tel"
            inputMode="numeric"
            pattern="[0-9]*"
            value={formData.phoneNumber}
            onChange={(e) => {
              // Only allow numeric input and limit to 10 digits
              const value = e.target.value.replace(/\D/g, '').slice(0, 10);
              setFormData({ ...formData, phoneNumber: value });
            }}
            onKeyPress={(e) => {
              // Prevent non-numeric characters from being typed
              if (!/[0-9]/.test(e.key)) {
                e.preventDefault();
              }
            }}
            className="w-full px-4 py-3.5 border-2 border-slate-200 rounded-xl focus:ring-4 focus:ring-teal-100 focus:border-teal-500 transition-all duration-200 text-lg group-hover:border-slate-300"
            placeholder="05XXXXXXXX"
            dir="ltr"
            maxLength={10}
          />
          <p className="text-xs text-slate-500 mt-1">יש להזין 10 ספרות בלבד</p>
        </div>
        <div className="relative group">
          <label className="block text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
            <svg className="w-4 h-4 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            יישוב *
          </label>
          <input
            type="text"
            value={formData.village}
            onChange={(e) => {
              setFormData({ ...formData, village: e.target.value });
              setShowVillageDropdown(true);
            }}
            onFocus={() => setShowVillageDropdown(true)}
            className="w-full px-4 py-3.5 border-2 border-slate-200 rounded-xl focus:ring-4 focus:ring-teal-100 focus:border-teal-500 transition-all duration-200 text-lg group-hover:border-slate-300"
            placeholder="התחל להקליד שם יישוב..."
          />
          
          {showVillageDropdown && filteredVillages.length > 0 && (
            <div className="absolute z-10 w-full mt-2 bg-white border-2 border-teal-200 rounded-xl shadow-2xl max-h-60 overflow-y-auto animate-slide-down">
              {filteredVillages.map((village) => (
                <div
                  key={village.id}
                  onClick={() => {
                    setFormData({ ...formData, village: village.name });
                    setShowVillageDropdown(false);
                  }}
                  className="px-4 py-3 hover:bg-gradient-to-r hover:from-teal-50 hover:to-blue-50 cursor-pointer transition-all border-b border-slate-100 last:border-0"
                >
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    </svg>
                    <span className="font-medium text-slate-700">{village.name}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>


      </div>
    </div>
  );

  const renderStep2ServiceType = () => (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-gradient-to-br from-teal-400 to-blue-500 rounded-lg flex items-center justify-center">
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
          </svg>
        </div>
        <h3 className="text-2xl font-bold text-slate-800">בחר סוג שירות</h3>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <button
          onClick={() => setFormData({ ...formData, serviceType: 'US' })}
          className={`group relative p-8 border-3 rounded-2xl transition-all duration-300 transform hover:scale-105 ${
            formData.serviceType === 'US'
              ? 'border-teal-500 bg-gradient-to-br from-teal-50 to-blue-50 shadow-xl shadow-teal-100'
              : 'border-slate-200 hover:border-teal-300 hover:shadow-lg bg-white'
          }`}
        >
          <div className="text-center">
            <div className={`inline-flex items-center justify-center w-20 h-20 rounded-2xl mb-4 transition-all duration-300 ${
              formData.serviceType === 'US'
                ? 'bg-gradient-to-br from-teal-400 to-blue-500 shadow-lg'
                : 'bg-gradient-to-br from-slate-200 to-slate-300 group-hover:from-teal-300 group-hover:to-blue-400'
            }`}>
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
              </svg>
            </div>
            <h4 className="text-2xl font-bold text-slate-800 mb-2">אולטרסאונד</h4>
            <p className="text-sm text-slate-600 font-medium">Ultrasound (US)</p>
            <p className="text-xs text-slate-500 mt-2">בדיקות אולטרה-סאונד מתקדמות</p>
          </div>
          {formData.serviceType === 'US' && (
            <div className="absolute top-4 left-4 w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center animate-scale-in">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          )}
        </button>

        <button
          onClick={() => setFormData({ ...formData, serviceType: 'Xray' })}
          className={`group relative p-8 border-3 rounded-2xl transition-all duration-300 transform hover:scale-105 ${
            formData.serviceType === 'Xray'
              ? 'border-blue-500 bg-gradient-to-br from-blue-50 to-indigo-50 shadow-xl shadow-blue-100'
              : 'border-slate-200 hover:border-blue-300 hover:shadow-lg bg-white'
          }`}
        >
          <div className="text-center">
            <div className={`inline-flex items-center justify-center w-20 h-20 rounded-2xl mb-4 transition-all duration-300 ${
              formData.serviceType === 'Xray'
                ? 'bg-gradient-to-br from-blue-400 to-indigo-500 shadow-lg'
                : 'bg-gradient-to-br from-slate-200 to-slate-300 group-hover:from-blue-300 group-hover:to-indigo-400'
            }`}>
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <h4 className="text-2xl font-bold text-slate-800 mb-2">צילום רנטגן</h4>
            <p className="text-sm text-slate-600 font-medium">X-Ray</p>
            <p className="text-xs text-slate-500 mt-2">צילומי רנטגן דיגיטליים</p>
          </div>
          {formData.serviceType === 'Xray' && (
            <div className="absolute top-4 left-4 w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center animate-scale-in">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          )}
        </button>
      </div>
    </div>
  );

  const renderStep3Services = () => (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-gradient-to-br from-teal-400 to-blue-500 rounded-lg flex items-center justify-center">
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>
        </div>
        <h3 className="text-2xl font-bold text-slate-800">בחר שירות</h3>
      </div>
      
      {loading ? (
        <div className="text-center py-16">
          <div className="relative inline-block">
            <div className="w-16 h-16 border-4 border-teal-200 border-t-teal-600 rounded-full animate-spin"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <svg className="w-8 h-8 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            </div>
          </div>
          <p className="mt-6 text-lg font-medium text-slate-700">טוען שירותים...</p>
          <p className="text-sm text-slate-500 mt-2">אנא המתן</p>
        </div>
      ) : (
        <div className="max-h-96 overflow-y-auto pr-2 custom-scrollbar">
          <div className="grid grid-cols-1 gap-4">
            {services.map((service) => (
              <button
                key={service.id}
                onClick={() =>
                  setFormData({
                    ...formData,
                    serviceId: service.id,
                    serviceName: service.name,
                  })
                }
                className={`p-5 border-2 rounded-xl text-right transition-all duration-300 transform hover:scale-102 ${
                  formData.serviceId === service.id
                    ? 'border-teal-500 bg-gradient-to-r from-teal-50 to-blue-50 shadow-lg'
                    : 'border-slate-200 hover:border-teal-300 hover:shadow-md bg-white'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="text-lg font-bold text-slate-800 mb-2">{service.name}</h4>
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <svg className="w-4 h-4 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span>זמין בימים: {service.availableDays.map(d => WEEK_DAYS.find(w => w.id === d)?.label).join(', ')}</span>
                    </div>
                  </div>
                  {formData.serviceId === service.id && (
                    <div className="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center animate-scale-in">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderXrayWorkingHours = () => {
    const hebrewDays: { [key: string]: string } = {
      'Sunday': 'ראשון',
      'Monday': 'שני',
      'Tuesday': 'שלישי',
      'Wednesday': 'רביעי',
      'Thursday': 'חמישי',
      'Friday': 'שישי',
      'Saturday': 'שבת'
    };

    const today = new Date();
    const todayDay = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][today.getDay()];
    
    // Sort schedule to show today first, then Sunday-Friday, then Saturday last
    const sortedSchedule = [...xraySchedule].sort((a, b) => {
      if (a.dayId === todayDay) return -1;
      if (b.dayId === todayDay) return 1;
      
      // Saturday goes to the end
      if (a.dayId === 'Saturday') return 1;
      if (b.dayId === 'Saturday') return -1;
      
      const dayOrder = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      return dayOrder.indexOf(a.dayId) - dayOrder.indexOf(b.dayId);
    });

    return (
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-lg flex items-center justify-center">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <h3 className="text-2xl font-bold text-slate-800">שעות פעילות - צילום רנטגן</h3>
          </div>
        </div>

        <div className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h4 className="text-lg font-bold text-blue-900 mb-2">שים לב</h4>
              <p className="text-blue-800 leading-relaxed">
                לשירות צילום רנטגן, ניתן להגיע למרפאה בשעות הפעילות ללא קביעת תור מראש. המתן לתורך במקום.
              </p>
              <p className="text-blue-800 leading-relaxed mt-2" dir="rtl">
                خدمة الأشعة متاحة بدون حجز مسبق. تعال بأوقات الدوام واستنى دورك بالمكان.
              </p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-16">
            <div className="relative inline-block">
              <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
            </div>
            <p className="mt-6 text-lg font-medium text-slate-700">טוען שעות פעילות...</p>
          </div>
        ) : sortedSchedule.length === 0 ? (
          <div className="text-center py-12 bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-2xl">
            <p className="text-lg font-bold text-amber-800">אין מידע על שעות פעילות</p>
          </div>
        ) : (
          <div className="bg-white border-2 border-slate-200 rounded-xl overflow-hidden shadow-sm">
            <table className="w-full">
              <thead>
                <tr className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b-2 border-slate-200">
                  <th className="px-6 py-4 text-right text-sm font-bold text-slate-800">יום</th>
                  <th className="px-6 py-4 text-right text-sm font-bold text-slate-800">שעות פעילות</th>
                </tr>
              </thead>
              <tbody>
                {sortedSchedule.map((day) => {
                  const isToday = day.dayId === todayDay;
                  
                  return (
                    <tr
                      key={day.dayId}
                      className={`border-b border-slate-100 last:border-0 ${
                        isToday
                          ? 'bg-gradient-to-r from-teal-50 to-blue-50'
                          : day.isOpen
                          ? 'bg-white hover:bg-slate-50'
                          : 'bg-slate-50'
                      }`}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          {isToday && (
                            <div className="px-2 py-1 bg-teal-500 text-white text-xs font-bold rounded-full">
                              היום
                            </div>
                          )}
                          <span className={`text-lg font-bold ${isToday ? 'text-teal-700' : 'text-slate-800'}`}>
                            יום {hebrewDays[day.dayId]}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {day.isOpen && day.workingHours && day.workingHours.length > 0 ? (
                          <div className="space-y-1">
                            {day.workingHours.map((hours: any, idx: number) => (
                              <div key={idx} className="flex items-center gap-2 text-slate-700">
                                <svg className="w-4 h-4 text-blue-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span className="text-base font-semibold" dir="ltr">
                                  {hours.startTime} - {hours.endTime}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-red-600 text-sm font-semibold">סגור</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  const renderDays = () => {
    const hebrewDays: { [key: string]: string } = {
      'Sunday': 'ראשון',
      'Monday': 'שני',
      'Tuesday': 'שלישי',
      'Wednesday': 'רביעי',
      'Thursday': 'חמישי',
      'Friday': 'שישי',
      'Saturday': 'שבת'
    };

    return (
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-gradient-to-br from-teal-400 to-blue-500 rounded-lg flex items-center justify-center">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <div>
            <h3 className="text-2xl font-bold text-slate-800">בחר תאריך</h3>
            <p className="text-sm text-slate-600">מציג רק תאריכים עם תורים פנויים</p>
          </div>
        </div>
        
        {loading ? (
          <div className="text-center py-16">
            <div className="relative inline-block">
              <div className="w-16 h-16 border-4 border-teal-200 border-t-teal-600 rounded-full animate-spin"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <svg className="w-8 h-8 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
            <p className="mt-6 text-lg font-medium text-slate-700">טוען תאריכים זמינים...</p>
            <p className="text-sm text-slate-500 mt-2">אנא המתן</p>
          </div>
        ) : availableDates.length === 0 ? (
          <div className="text-center py-12 bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-2xl">
            <svg className="w-16 h-16 mx-auto text-amber-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-lg font-bold text-amber-800 mb-2">אין תאריכים פנויים כרגע</p>
            <p className="text-sm text-amber-700">אנא נסה שוב מאוחר יותר</p>
          </div>
        ) : (
          <div className="max-h-[500px] overflow-y-auto px-2">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 sm:gap-3">
              {availableDates.map((dateObj) => {
                const dateStr = new Date(dateObj.date);
                const dayOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][dateStr.getDay()];
                const hebrewDay = hebrewDays[dayOfWeek] || hebrewDays[dateObj.dayName];
                const isToday = dateObj.date === new Date().toISOString().split('T')[0];
                const displayDate = dateStr.toLocaleDateString('he-IL', {
                  day: 'numeric',
                  month: 'short'
                });
                const fullDate = `${hebrewDay}, ${dateStr.toLocaleDateString('he-IL', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                })}`;
                
                return (
                  <button
                    key={dateObj.date}
                    onClick={() =>
                      setFormData({
                        ...formData,
                        selectedDay: dateObj.date,
                        selectedDayLabel: fullDate,
                      })
                    }
                    className={`relative group p-4 border-2 rounded-xl transition-all duration-300 transform hover:scale-105 ${
                      formData.selectedDay === dateObj.date
                        ? 'border-teal-500 bg-gradient-to-br from-teal-50 to-blue-50 shadow-lg'
                        : 'border-slate-200 hover:border-teal-300 hover:shadow-md bg-white'
                    }`}
                  >
                    <div className="text-center">
                      {isToday && (
                        <div className="text-xs font-bold text-teal-600 mb-1">היום</div>
                      )}
                      <div className={`text-sm font-medium mb-1 ${
                        formData.selectedDay === dateObj.date ? 'text-teal-600' : 'text-slate-500'
                      }`}>
                        {hebrewDay}
                      </div>
                      <div className="text-lg font-bold text-slate-800">{displayDate}</div>
                      <div className="text-xs text-indigo-600 font-medium mt-1">
                        {dateObj.availableSlots} פנויים
                      </div>
                    </div>
                    {formData.selectedDay === dateObj.date && (
                      <div className="absolute top-1 left-1 w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center animate-scale-in">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderTimeSlots = () => (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-gradient-to-br from-teal-400 to-blue-500 rounded-lg flex items-center justify-center">
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <div>
          <h3 className="text-2xl font-bold text-slate-800">בחר שעה</h3>
          <p className="text-sm text-slate-600">יום נבחר: <span className="font-semibold text-teal-600">{formData.selectedDayLabel}</span></p>
        </div>
      </div>
      
      {loading ? (
        <div className="text-center py-16">
          <div className="relative inline-block">
            <div className="w-16 h-16 border-4 border-teal-200 border-t-teal-600 rounded-full animate-spin"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <svg className="w-8 h-8 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          <p className="mt-6 text-lg font-medium text-slate-700">טוען שעות זמינות...</p>
          <p className="text-sm text-slate-500 mt-2">אנא המתן</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
            {availableSlots.map((slot) => (
              <button
                key={slot.time}
                onClick={() => slot.isAvailable && setFormData({ ...formData, selectedTime: slot.time, intervalMinutes: slot.intervalMinutes || 5 })}
                disabled={!slot.isAvailable}
                className={`relative group p-4 border-2 rounded-xl transition-all duration-300 transform ${
                  !slot.isAvailable
                    ? 'border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed opacity-50'
                    : formData.selectedTime === slot.time
                    ? 'border-teal-500 bg-gradient-to-br from-teal-50 to-blue-50 text-teal-700 font-bold shadow-lg scale-105'
                    : 'border-slate-200 hover:border-teal-300 hover:shadow-md text-slate-800 bg-white hover:scale-105'
                }`}
              >
                <div className="text-center">
                  <svg className={`w-5 h-5 mx-auto mb-1 ${
                    !slot.isAvailable ? 'text-slate-300' : 
                    formData.selectedTime === slot.time ? 'text-teal-600' : 
                    'text-slate-400 group-hover:text-teal-500'
                  }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="text-lg font-bold">{slot.time}</div>
                  {!slot.isAvailable && (
                    <div className="text-xs text-red-500 mt-1 font-medium">תפוס</div>
                  )}
                </div>
                {formData.selectedTime === slot.time && (
                  <div className="absolute -top-1 -right-1 w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center animate-scale-in shadow-lg">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}
              </button>
            ))}
          </div>
          
          {availableSlots.filter(s => s.isAvailable).length === 0 && !loading && (
            <div className="text-center py-12 bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-2xl animate-slide-down">
              <svg className="w-16 h-16 mx-auto text-amber-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-lg font-bold text-amber-800 mb-2">אין שעות פנויות ביום זה</p>
              <p className="text-sm text-amber-700">אנא בחר יום אחר</p>
            </div>
          )}
        </>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-blue-50 to-indigo-50">
      {/* Duplicate Appointment Modal */}
      {showDuplicateModal && duplicateAppointment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 animate-scale-in">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-2xl font-bold text-red-600">⚠️ תור קיים</h3>
              <button
                onClick={() => {
                  setShowDuplicateModal(false);
                  setDuplicateAppointment(null);
                }}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-red-800 font-semibold mb-3">
                למטופל קיים כבר תור עתידי לאותו שירות:
              </p>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">שם:</span>
                  <span className="font-semibold">{duplicateAppointment.fullName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">טלפון:</span>
                  <span className="font-semibold" dir="ltr">{duplicateAppointment.phoneNumber}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">שירות:</span>
                  <span className="font-semibold">{duplicateAppointment.serviceName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">תאריך:</span>
                  <span className="font-semibold">{duplicateAppointment.selectedDayLabel || duplicateAppointment.selectedDay}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">שעה:</span>
                  <span className="font-semibold" dir="ltr">{duplicateAppointment.selectedTime}</span>
                </div>
                {duplicateAppointment.idCard && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">ת.ז.:</span>
                    <span className="font-semibold" dir="ltr">{duplicateAppointment.idCard}</span>
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex justify-center">
              <button
                onClick={() => {
                  setShowDuplicateModal(false);
                  setDuplicateAppointment(null);
                  // Reset form to start new appointment
                  setIdCardTouched(false);
                  setFormData({
                    fullName: '',
                    phoneNumber: '',
                    idCard: '',
                    village: '',
                    serviceType: null,
                    serviceId: '',
                    serviceName: '',
                    selectedDay: '',
                    selectedDayLabel: '',
                    selectedTime: '',
                  });
                  setDraftId(undefined);
                  setCurrentStep(1);
                  setError(null);
                }}
                className="px-8 py-3 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 transition-colors"
              >
                סגור
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div className="max-w-4xl mx-auto px-2 sm:px-4 py-4 sm:py-8">
        {/* Page Title with Animation */}
        <div className="text-center mb-4 sm:mb-8 animate-fade-in">
          <h2 className="text-2xl sm:text-4xl font-bold text-slate-800 mb-2">קביעת תור חדש</h2>
          <p className="text-sm sm:text-base text-slate-600">מלא את הפרטים הבאים לקביעת תור</p>
        </div>

        {/* Main Card with Animation */}
        <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl p-3 sm:p-8 border border-slate-100 animate-slide-up">
          {currentStep === 0 ? (
            // Success screen - blank with centered success message
            <div className="min-h-[600px] flex items-center justify-center">
              <div className="text-center animate-fade-in">
                <div className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6 animate-scale-in">
                  <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h2 className="text-3xl font-bold text-green-800 mb-2">התור נקבע בהצלחה!</h2>
                <p className="text-lg text-green-600">חוזר למסך הראשי...</p>
              </div>
            </div>
          ) : (
            <>
              {renderStepIndicator()}

              {error && (
                <div className="mb-6 p-4 bg-red-50 border-r-4 border-red-500 text-red-700 rounded-lg flex items-start gap-3 animate-shake">
                  <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{error}</span>
                </div>
              )}

              {/* Step Content */}
              <div className="min-h-[450px] transition-all duration-300">
                {currentStep === 1 && renderStep1()}
                {currentStep === 2 && renderStep2ServiceType()}
                {currentStep === 3 && formData.serviceType === 'US' && renderStep3Services()}
                {currentStep === 3 && formData.serviceType === 'Xray' && renderXrayWorkingHours()}
                {currentStep === 4 && formData.serviceType === 'US' && renderDays()}
                {currentStep === 4 && formData.serviceType === 'Xray' && renderTimeSlots()}
                {currentStep === 5 && formData.serviceType === 'US' && renderTimeSlots()}
              </div>

              {/* Navigation Buttons */}
              <div className="flex flex-col sm:flex-row justify-between gap-3 sm:gap-4 mt-6 sm:mt-10 pt-4 sm:pt-8 border-t-2 border-slate-100">
                <button
                  onClick={handleBack}
                  disabled={currentStep === 1}
                  className="group flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-white border-2 border-slate-300 text-slate-700 rounded-xl font-semibold hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white transform hover:scale-105 disabled:hover:scale-100 order-2 sm:order-1"
                >
                  <svg className="w-5 h-5 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  חזור
                </button>

                {currentStep < totalSteps ? (
                  <button
                    onClick={handleNext}
                    disabled={loading || !isCurrentStepValid()}
                    className="group flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-xl font-semibold hover:from-teal-600 hover:to-blue-700 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 disabled:hover:scale-100 order-1 sm:order-2"
                  >
                    {loading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        טוען...
                      </>
                    ) : (
                      <>
                        המשך
                        <svg className="w-5 h-5 transform group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </>
                    )}
                  </button>
                ) : (
                  <button
                    onClick={handleSubmit}
                    disabled={loading || (formData.serviceType !== 'Xray' && !formData.selectedTime)}
                    className="group flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-semibold hover:from-green-600 hover:to-emerald-700 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 disabled:hover:scale-100 order-1 sm:order-2"
                  >
                    {loading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        שומר...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        {formData.serviceType === 'Xray' ? 'לדף הבית' : 'אישור תור'}
                      </>
                    )}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingFlow;
